/**
 * currency-price-label-fix.js
 *
 * Some variant option labels (e.g. "160 invites - $902 NZD") have the price
 * baked in as plain text on the option value itself - on the product page's
 * "Number of Invites" dropdown, and on the same option text shown again in
 * the cart page and cart drawer. Shopify's multi-currency conversion only
 * converts real numeric price fields (variant price, cart totals, app addon
 * prices) - it can't rewrite arbitrary text sitting inside an option name.
 * That leaves these labels stuck in NZD even when a shopper is browsing in
 * another presentment currency, while the surrounding real prices convert
 * correctly.
 *
 * This script finds every one of those baked-in "$X NZD" labels - on the
 * product page and in the cart/cart drawer - and rewrites the visible text
 * to the shopper's active currency. For accuracy it fetches the relevant
 * product's own JSON (/products/<handle>.js), which returns each variant's
 * price already converted server-side by Shopify - the exact same number
 * used elsewhere on the page - so every label matches to the cent. If that
 * fetch is unavailable for any reason, it falls back to a simple rate-based
 * estimate using window.Shopify.currency.
 *
 * It never touches any underlying option "value" or variant id used for
 * variant matching / cart behaviour - only visible text - so it can't affect
 * add-to-cart, checkout, or pricing logic.
 */
(function () {
  var BASE_CURRENCY = 'NZD';
  var PRICE_PATTERN = /\$([\d,]+(?:\.\d{1,2})?)\s*NZD\b/g;
  var PROCESSED_ATTR = 'data-currency-label-fixed';
  var productPriceMapCache = {}; // productUrl -> Promise<{ byId: {}, byTitle: {} }>

  function formatConverted(amount, currencyCode) {
    var formattedNumber = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return '$' + formattedNumber + ' ' + currencyCode;
  }

  function rewriteWithRate(text, rate, activeCurrency) {
    PRICE_PATTERN.lastIndex = 0;
    return text.replace(PRICE_PATTERN, function (match, num) {
      var base = parseFloat(num.replace(/,/g, ''));
      if (isNaN(base)) return match;
      return formatConverted(base * rate, activeCurrency);
    });
  }

  function rewriteWithExactAmount(text, amount, activeCurrency) {
    PRICE_PATTERN.lastIndex = 0;
    var replacement = formatConverted(amount, activeCurrency);
    // Use a replacer function, not a string: a string replacement would let
    // JS interpret "$1" etc. in the formatted amount as a backreference to
    // the regex's capture group, corrupting any amount starting with "$1,xxx".
    return text.replace(PRICE_PATTERN, function () {
      return replacement;
    });
  }

  function getVariantPriceMap(productUrl) {
    if (!productPriceMapCache[productUrl]) {
      productPriceMapCache[productUrl] = fetch(productUrl + '.js', { credentials: 'same-origin' })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (data) {
          var map = { byId: {}, byTitle: {} };
          if (data && data.variants) {
            data.variants.forEach(function (v) {
              // v.price is in cents, already converted to the active presentment currency.
              map.byId[String(v.id)] = v.price;
              map.byTitle[v.title] = v.price;
            });
          }
          return map;
        })
        .catch(function () {
          return { byId: {}, byTitle: {} };
        });
    }
    return productPriceMapCache[productUrl];
  }

  function getOptionValueFromNode(node) {
    if (node.tagName === 'OPTION') return node.value;
    var label = node.closest('label');
    if (label) {
      var forId = label.getAttribute('for');
      if (forId) {
        var input = document.getElementById(forId);
        if (input) return input.value;
      }
    }
    return node.textContent.trim();
  }

  // Gather every element that might contain a baked-in "$X NZD" label,
  // paired with how to look up its true converted price.
  function collectCandidates() {
    var candidates = [];

    // 1) Product-page variant pickers (dropdown and button/swatch styles).
    document
      .querySelectorAll('variant-selects option, variant-radios .label-swatch, variant-radios .tooltip')
      .forEach(function (node) {
        var picker = node.closest('variant-selects, variant-radios');
        var productUrl = picker && picker.getAttribute('data-url');
        if (!productUrl) return;
        candidates.push({
          node: node,
          productUrl: productUrl,
          lookupType: 'title',
          lookupKey: getOptionValueFromNode(node)
        });
      });

    // 2) Cart page and cart drawer line items. Each option value is
    // rendered as a plain <dd> inside that line's row - there's no variant
    // id or product handle on the row itself, so resolve the product URL
    // from that same row's own product link (present for the image and/or
    // title) and match by the option's raw text, same as the PDP picker.
    document.querySelectorAll('dd').forEach(function (dd) {
      var row = dd.closest('tr, li, [class*="cart-item"], [class*="horizontal-product"]');
      if (!row) return;
      var link = row.querySelector('a[href*="/products/"]');
      if (!link) return;
      var productUrl;
      try {
        productUrl = new URL(link.getAttribute('href'), window.location.origin).pathname;
      } catch (e) {
        return;
      }
      candidates.push({
        node: dd,
        productUrl: productUrl,
        lookupType: 'title',
        lookupKey: dd.textContent.trim()
      });
    });

    return candidates;
  }

  function convertPriceLabels() {
    try {
      var shopifyCurrency = window.Shopify && window.Shopify.currency;
      if (!shopifyCurrency || !shopifyCurrency.active) return;

      var activeCurrency = shopifyCurrency.active;
      if (activeCurrency === BASE_CURRENCY) return; // nothing to convert

      var rate = parseFloat(shopifyCurrency.rate);
      var hasFallbackRate = !isNaN(rate) && rate > 0;

      collectCandidates().forEach(function (candidate) {
        var node = candidate.node;
        if (node.hasAttribute(PROCESSED_ATTR)) return;

        var text = node.textContent;
        PRICE_PATTERN.lastIndex = 0;
        if (!PRICE_PATTERN.test(text)) return;

        // Claim this node immediately so we never process it twice, even
        // while an async lookup for it is still in flight.
        node.setAttribute(PROCESSED_ATTR, 'true');

        var applyFallback = function () {
          if (!hasFallbackRate) return;
          var newText = rewriteWithRate(text, rate, activeCurrency);
          if (newText !== text) node.textContent = newText;
        };

        getVariantPriceMap(candidate.productUrl)
          .then(function (map) {
            var cents = candidate.lookupType === 'id'
              ? map.byId[String(candidate.lookupKey)]
              : map.byTitle[candidate.lookupKey];
            if (typeof cents === 'number') {
              var newText = rewriteWithExactAmount(text, cents / 100, activeCurrency);
              node.textContent = newText;
            } else {
              applyFallback();
            }
          })
          .catch(applyFallback);
      });
    } catch (e) {
      // Never let a display nicety break the page.
      if (window.console && console.warn) {
        console.warn('currency-price-label-fix:', e);
      }
    }
  }

  function init() {
    convertPriceLabels();

    // Catch variant pickers and cart rows rendered after initial load
    // (AJAX cart drawer updates, quick-view modals, etc.).
    if (window.MutationObserver) {
      var observer = new MutationObserver(function () {
        convertPriceLabels();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
